import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Search, X } from "lucide-react";

export interface SearchInputOption<T = any> {
  value: string;
  label: string;
  data: T;
}

interface SearchInputProps<T = any> {
  label?: string;
  placeholder?: string;
  /** Controlled display value, e.g. the currently selected item's label */
  value?: string;
  /** Called with the trimmed query after the debounce window; return matching options */
  onSearch: (query: string) => Promise<SearchInputOption<T>[]>;
  /** Return `false` to decline the selection (e.g. a duplicate) — the input's
   *  displayed text then stays in sync with `value` instead of getting stuck
   *  on the declined option's label. */
  onSelect: (option: SearchInputOption<T>) => void | boolean;
  /** Custom row renderer for a result; falls back to plain label text */
  renderOption?: (option: SearchInputOption<T>) => React.ReactNode;
  /** Minimum characters typed before a search fires. Defaults to 0, so
   *  focusing an empty input shows an unfiltered "all items" list. */
  minChars?: number;
  debounceMs?: number;
  /** Clear the input after a selection instead of showing the picked label
   *  (useful for "add another" pickers like a product search) */
  clearOnSelect?: boolean;
  /** Called when the clear ("x") button is pressed, in addition to the
   *  input being emptied — use it to reset whatever id the parent stored
   *  from a previous onSelect, so the cleared text and the actual
   *  filter/selection stay in sync. */
  onClear?: () => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * Reusable debounced async search input: types → waits → calls onSearch →
 * shows results in a dropdown with an optional custom option renderer.
 * Used for supplier lookup (name/supplier ID) and product lookup
 * (name/SKU/HSN/product ID) — anywhere an id needs picking via API search
 * instead of preloading the full list into a <select>.
 */
function SearchInput<T = any>({
  label,
  placeholder = "Search...",
  value = "",
  onSearch,
  onSelect,
  renderOption,
  minChars = 0,
  debounceMs = 350,
  clearOnSelect = false,
  onClear,
  disabled = false,
  required = false,
  className = "",
}: SearchInputProps<T>) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<SearchInputOption<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  // Dropdown renders in a portal (so an ancestor's overflow/scroll clipping
  // can't cut it off) and flips above the input when there isn't enough
  // room below — same approach as the Popover component.
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Stay in sync when the parent resets/clears the selected value externally
  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Position the portaled dropdown against the input, flipping above it
  // when there isn't enough room below (e.g. near the bottom of a scroll
  // container or the viewport).
  useEffect(() => {
    const showDropdown = isOpen && query.trim().length >= minChars;
    if (!showDropdown) {
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      if (!inputWrapperRef.current) return;
      const rect = inputWrapperRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
      const vh = window.innerHeight;
      const gap = 4;

      let top: number;
      if (
        rect.bottom + gap + dropdownHeight > vh &&
        rect.top - gap - dropdownHeight >= 0
      ) {
        top = rect.top - gap - dropdownHeight;
      } else {
        top = rect.bottom + gap;
      }

      setCoords({ top, left: rect.left, width: rect.width });
    };

    // Place immediately (dropdown not mounted yet, so this assumes it fits
    // below), then re-measure once it's actually in the DOM and flip if not.
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, query, minChars, loading, results]);

  useEffect(() => {
    // Don't fetch until the user actually opens the field — avoids firing
    // a request the moment the component mounts.
    if (!isOpen) return;

    const trimmed = query.trim();
    if (trimmed.length < minChars) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    // Empty query (first open, nothing typed yet) shows the full list
    // instantly; anything typed after that debounces as normal.
    const delay = trimmed.length === 0 ? 0 : debounceMs;

    const timer = setTimeout(async () => {
      try {
        const res = await onSearch(trimmed);
        if (requestId === requestIdRef.current) setResults(res);
      } catch (err) {
        console.error("SearchInput: search failed", err);
        if (requestId === requestIdRef.current) setResults([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, minChars, debounceMs, isOpen]);

  const handleSelect = (option: SearchInputOption<T>) => {
    const accepted = onSelect(option);
    setResults([]);
    setIsOpen(false);
    if (accepted === false) return;
    setQuery(clearOnSelect ? "" : option.label);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    onClear?.();
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col space-y-1 ${className}`}
    >
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}

      <div className="relative" ref={inputWrapperRef}>
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          className="w-full border rounded-md pl-8 pr-8 py-2 text-sm border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:disabled:bg-gray-900"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {loading ? (
          <Loader2
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
          />
        ) : (
          query &&
          !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )
        )}
      </div>

      {isOpen &&
        query.trim().length >= minChars &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto dark:bg-gray-800 dark:border-gray-700"
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              width: coords?.width,
              visibility: coords ? "visible" : "hidden",
            }}
          >
            {loading && results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No results found
              </div>
            ) : (
              results.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                >
                  {renderOption ? renderOption(option) : option.label}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SearchInput;
